import * as React from 'react';
import { numericOnly } from '../InvoicingUtils';
import { GcpExtraCostType, GcpMaterialEntry, GcpMaterialsHandlers } from '../types';

// Shared "each GCP service item supports one GCP Materials and one Mileage extra cost entry,
// keyed by the item's gcpServiceId (or its synthesized id as a fallback)" mechanism used by
// both the pilot submission form and the office invoice review/fix form. `initial` is a lazy
// initializer for pre-populating entries already saved on an invoice being edited.
export function useGcpMaterialEntries(initial?: () => Record<string, GcpMaterialEntry[]>) {
  const [entries, setEntries] = React.useState<Record<string, GcpMaterialEntry[]>>(() => initial?.() ?? {});

  const addGcpMaterial = React.useCallback((gcpItemKey: string, type: GcpExtraCostType) => {
    setEntries((prev) => {
      const existing = prev[gcpItemKey] ?? [];
      if (existing.some((e) => e.type === type)) return prev;
      return { ...prev, [gcpItemKey]: [...existing, { id: crypto.randomUUID(), type, amount: '' }] };
    });
  }, []);

  const removeGcpMaterial = React.useCallback((gcpItemKey: string, entryId: string) => {
    setEntries((prev) => ({
      ...prev,
      [gcpItemKey]: (prev[gcpItemKey] ?? []).filter((e) => e.id !== entryId),
    }));
  }, []);

  const updateGcpMaterialAmount = React.useCallback((gcpItemKey: string, entryId: string, value: string) => {
    setEntries((prev) => ({
      ...prev,
      [gcpItemKey]: (prev[gcpItemKey] ?? []).map((e) => (e.id === entryId ? { ...e, amount: numericOnly(value) } : e)),
    }));
  }, []);

  const handlers: GcpMaterialsHandlers = React.useMemo(
    () => ({ entries, onAdd: addGcpMaterial, onRemove: removeGcpMaterial, onAmountChange: updateGcpMaterialAmount }),
    [entries, addGcpMaterial, removeGcpMaterial, updateGcpMaterialAmount],
  );

  const hasEntries = React.useMemo(() => Object.values(entries).some((e) => e.length > 0), [entries]);

  const total = React.useMemo(
    () =>
      Object.values(entries)
        .flat()
        .reduce((s, e) => s + parseFloat(e.amount || '0'), 0),
    [entries],
  );

  return { entries, addGcpMaterial, removeGcpMaterial, updateGcpMaterialAmount, handlers, hasEntries, total };
}
